import { useState } from 'react';
import {
  Button,
  Dropdown,
  Nav,
  Navbar,
  NavDropdown,
  Offcanvas,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { faBullhorn } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import optainLogoImg from 'src/assets/icons/logo/optain.png';
import {
  APP_EVENTS,
  captureAppEvent,
  LOGOUT_TRIGGER,
} from 'src/services/posthogEvents';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CameraConnectivity from 'src/components/cameraConnectivity';
import ChangePwdModal from 'src/components/custom_components/ChangePwdModal';

import { Api } from 'src/models/Api.model';
import { I18NextNS } from 'src/models/Language.model';

import useCheckNavigation from 'src/hooks/useCheckNavigation';
import { useCheckNavItemVisibility } from 'src/hooks/useCheckNavItemVisibility';

import formatUserRole from 'src/utils/formatUserRole';

const Header = () => {
  const [showChangePassword, setShowChangePassword] = useState(false);

  const handlePwdOpen = () => setShowChangePassword(true);
  const handlePwdClose = () => setShowChangePassword(false);

  const { currentUser } = useRemoteBoundStore();
  const { checkNavigation } = useCheckNavigation();

  const userType = currentUser?.currentRole?.role;

  const fullName = currentUser?.fullName;

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const {
    logOut,
    cameraData,
    practiceSettings: { broadcast },
  } = useRemoteBoundStore();

  const handleLogout = () => {
    Api.confirmBox({
      title: t('bootbox.title.Confirm', { ns: I18NextNS.COMMON }),
      message: t('loginLogout.confirmLogout'),
      callback: (result: boolean) => {
        if (!result) {
          return;
        }

        captureAppEvent(APP_EVENTS.USER_LOGGED_OUT, {
          trigger: LOGOUT_TRIGGER.USER,
        });
        logOut();
      },
      locale: language,
    });
  };

  const location = useLocation();

  const { isShowNewExamPage, isShowResultsPage, isShowUsersPage } =
    useCheckNavItemVisibility();

  return (
    <>
      <Navbar key="lg" expand="lg">
        <Navbar.Brand href="#" data-track="decorative.header.logo">
          <div style={{ height: '58px' }}>
            <img
              src={optainLogoImg}
              alt="Logo"
              className="mh-100 object-fit-contain"
            />
          </div>
        </Navbar.Brand>

        {cameraData && <CameraConnectivity />}

        {broadcast && (
          <Button
            variant="warning"
            className="ms-3"
            data-test="navbar-broadcast-button"
            data-track="decorative.header.broadcast"
          >
            <FontAwesomeIcon icon={faBullhorn} className="me-2" />
            {broadcast}
          </Button>
        )}

        <Navbar.Toggle aria-controls="basic-navbar-nav" />

        <Navbar.Offcanvas
          id="basic-navbar-nav"
          aria-labelledby="basic-navbar-nav-expand"
          placement="end"
          // Its focus trap would fight the dialog opened from the menu.
          enforceFocus={false}
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title id="basic-navbar-nav-expand">
              <h2>Optain</h2>
            </Offcanvas.Title>
          </Offcanvas.Header>

          <Offcanvas.Body>
            <Nav className="justify-content-end flex-grow-1 ps-3 gap-2">
              {isShowNewExamPage && (
                <Nav.Link
                  href="#/session-form"
                  className="d-md-block d-lg-none text-start border text-black"
                  as={Button}
                  variant="light"
                  onClick={checkNavigation}
                >
                  {t('tabs.newExam', {
                    ns: I18NextNS.COMMON,
                  })}
                </Nav.Link>
              )}

              {isShowResultsPage && (
                <Nav.Link
                  href="#/results"
                  className="d-md-block d-lg-none text-start border text-black"
                  as={Button}
                  variant="light"
                  onClick={checkNavigation}
                >
                  {t('tabs.results', { ns: I18NextNS.COMMON })}
                </Nav.Link>
              )}

              <Nav.Link
                href="#/settings"
                className="d-md-block d-lg-none text-start border text-black"
                as={Button}
                variant="light"
                onClick={checkNavigation}
              >
                {t('tabs.settings', { ns: I18NextNS.COMMON })}
              </Nav.Link>

              {isShowUsersPage && (
                <Nav.Link
                  href="#/subuser"
                  className="d-md-block d-lg-none text-start border text-black"
                  as={Button}
                  variant="light"
                  onClick={checkNavigation}
                >
                  {t('tabs.users', { ns: I18NextNS.COMMON })}
                </Nav.Link>
              )}

              <Nav.Link
                href="#/about"
                active={location.pathname === '/about'}
                className="d-md-block text-start border text-black"
                onClick={checkNavigation}
                as={Button}
                variant="light"
                data-track="navigate.about"
              >
                {t('tabs.about', { ns: I18NextNS.COMMON })}
              </Nav.Link>

              <Dropdown align="end">
                <Dropdown.Toggle
                  variant="outline-light"
                  className="w-100 border border-1 text-start"
                  data-test="user-dropdown-toggle"
                >
                  <>
                    <span className="text-black fw-medium">
                      {userType
                        ? t(
                            `patientFields.userRoleEnum.${formatUserRole(userType)}`,
                            {
                              ns: I18NextNS.COMMON,
                            },
                          )
                        : t(`users.user`, {
                            ns: I18NextNS.PAGES,
                          })}
                      :
                    </span>
                    <span className="ms-2 text-black text-truncate">
                      {fullName}
                    </span>
                  </>
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <NavDropdown.Item
                    onClick={handlePwdOpen}
                    data-test="navbar-change-password-menu-item"
                    data-track="auth.change-password.open"
                  >
                    {t('changePassword.title')}
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item onClick={handleLogout}>
                    {t('loginLogout.logout')}
                  </NavDropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Nav>
          </Offcanvas.Body>
        </Navbar.Offcanvas>
      </Navbar>
      <ChangePwdModal
        isShowChangePwdModal={showChangePassword}
        handleCloseChangePwdModal={handlePwdClose}
        canBeCanceled
      />
    </>
  );
};

export default Header;
